import type { ConsentText, FormDoc, LetterDoc, SenderInfo, Values } from "@gsignatures/core";
import demoLetter from "./demo-letter.json";

/** The prototype's sample people and numbers. Nothing here is real. */

export const FAKE_PACK = "irs.w8ben@2021-10";

export const FAKE_SENDER: SenderInfo = {
  name: "Golden Sparrow",
  legalName: "Golden Sparrow LLC",
  color: "#f4b23a",
  contactName: "Neha Kapoor",
  contactEmail: "neha@goldensparrow.us",
};

export const FAKE_SIGNER = { name: "Priya Sharma", firstName: "Priya", maskedEmail: "p••••••a@gmail.com" };

/** Where the demo files are served from (public/demo). The letter is added by the lead; until then the viewer shows a placeholder. */
export const FAKE_FILES: Record<string, string> = {
  "doc-letter": "/demo/letter.pdf",
  "doc-w8ben": "/demo/w8ben-blank.pdf",
};

export function fakeConsent(): ConsentText {
  const s = FAKE_SENDER;
  return {
    version: "2026-10-24",
    sha256: "3c1f0e9a7b2d4c6e8f1a3b5c7d9e0f2a4b6c8d0e1f3a5b7c9d1e3f5a7b9c1d3e",
    title: "Sign electronically, or on paper — your choice",
    summary: [
      { icon: "paper", title: "Paper, any time", text: "You can ask for a paper copy of any document, free." },
      { icon: "undo", title: "Change your mind", text: "Switch to paper before you finish. No fee, nothing else changes." },
      { icon: "doc", title: "Only these documents", text: "This covers the offer letter and Form W-8BEN — nothing later." },
      { icon: "mail", title: "Keep your email current", text: "If your email changes, tell Neha so documents reach you." },
      { icon: "device", title: "What you need", text: "A current browser, a way to open PDFs, and your email." },
      { icon: "download", title: "Your copies", text: "Download everything when you finish. The letter is also emailed." },
    ],
    sections: [
      {
        heading: "Consent to electronic records and signatures",
        text: `${s.legalName} (“${s.name}”) asks you to receive and sign the documents listed below electronically, through gSignatures. Before you agree, please read this.`,
      },
      {
        heading: "What this covers",
        text: "Only this request: the Offer Letter (v3) and IRS Form W-8BEN. Any later document, such as a renewal of Form W-8BEN, will ask for your consent again.",
      },
      {
        heading: "Paper copies",
        text: `You can ask for a paper copy of any of these documents at any time, at no charge, by writing to ${s.contactName} at ${s.contactEmail}.`,
      },
      {
        heading: "Withdrawing your consent",
        text: `You can stop signing electronically at any time before you finish, by choosing “Sign on paper instead” in the menu or by telling ${s.contactName}. There is no fee, and the only consequence is that your documents continue on paper. Documents you have already signed stay signed.`,
      },
      { heading: "Keeping your contact details current", text: `If your email address changes, tell ${s.contactName} so documents reach you.` },
      {
        heading: "What you need",
        text: "A device with a current web browser (Chrome, Safari, Edge or Firefox released in the last two years), the ability to open PDF files, an email account, and a way to save or print files.",
      },
      { heading: "Your copies", text: "When you finish you can download every signed document. The signed offer letter is also emailed to you." },
    ],
    agreeLabel: "I've read this, and I agree to receive and sign these documents electronically.",
    agreeHelp: "I can open PDF files on this device.",
  };
}

export function fakeLetter(): LetterDoc {
  // The real letter, rendered from Golden Sparrow's seed template by api/scripts/demo-letter.ts.
  return {
    id: "doc-letter",
    kind: "letter",
    title: "Offer Letter",
    version: "v3",
    pages: demoLetter.pages,
    sha256: demoLetter.sha256,
    terms: demoLetter.terms,
    spots: demoLetter.spots as LetterDoc["spots"],
    companySigned: { name: "Prabhjot Singh", title: "Authorized signatory", at: "2026-10-24T16:14:00.000Z" },
    mergedSpots: demoLetter.mergedSpots,
  };
}

/** What the recruiter already knew about Priya — prefilled, and editable. */
export const FAKE_PREFILL: Values = {
  name: "Priya Sharma",
  citizenship: "IN",
  street: "Flat 4B, 12 MG Road",
  city: "Pune",
  state: "Maharashtra",
  postal: "411001",
  country: "IN",
  mailSame: true,
  treaty: false,
};

/** A complete, valid set of answers (for jumping straight to a later step in the demo). */
export const FAKE_COMPLETE: Values = { ...FAKE_PREFILL, ftin: "ABCPS1234K", dob: "04-17-1996" };

export function fakeForm(): FormDoc {
  return {
    id: "doc-w8ben",
    kind: "form",
    title: "Form W-8BEN",
    chooser: {
      question: "Are you a U.S. citizen, or a U.S. resident for tax purposes?",
      help: "You're a U.S. resident for tax purposes if you hold a U.S. green card, or if you were in the U.S. long enough to meet the IRS “substantial presence” test (generally 183 days over three years, counted in a specific way). If you're not sure, ask Neha Kapoor.",
      choices: [
        { value: "no", label: "No", detail: "You'll fill in Form W-8BEN (about 3 minutes).", pack: FAKE_PACK },
        { value: "yes", label: "Yes", detail: "You'll fill in Form W-9 instead.", pack: null },
      ],
    },
    values: { ...FAKE_PREFILL },
  };
}
